import {
  Button,
  Col,
  Form,
  FormInstance,
  Row,
  Space,
  Tabs,
  message,
  notification,
} from 'antd';
import { useCallback, useEffect, useState } from 'react';

import {
  CreateDataFeedConfigResponse,
  DataFeedConfig,
  DestinationType,
  FrequencyType,
} from '@cbinsights/externalapiservice/externalapiservice';
import { EntitySearchResult } from '@cbinsights/cbientityservice/cbientityservice';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { cleanRpcError } from 'client/modules/common/utils/rpcError';
import {
  DataFeedFirmographicsFilters,
  DataFeedFirmographicsForm,
  DataFeedForm,
  DataFeedFormField,
  SearchConfig,
  fileTypeToNumber,
} from './DataFeedForm';
import { V2FirmographicsIdsRequestBody } from '../services/apis';
import { useCreateDataFeedConfigMutation } from '../services/hooks';
import { useGetOptionalColumnCatalog } from './OptionalColumnsSelector';
import { FiltersTab, filtersCanContinue } from './FiltersTab';
import { DatasetTabForm, datasetCanContinue } from './DatasetTabForm';
import {
  DataFeedDestinationTabForm,
  DestinationTypeLabels,
  destinationCanContinue,
} from './DataFeedDestinationTabForm';
import {
  DataFeedFrequencyTabForm,
  frequencyCanContinue,
} from './DataFeedFrequencyTabForm';
import { DataFeedConfirmationTabForm } from './DataFeedConfirmationTabForm';
import { DataFeedFiles, filesCanContinue } from './DataFeedFiles';
import { DiscardDataFeedFormButton } from './CancelDataFeedFormButton';
import { DataFeedRoutes } from '../routesHelper';
import { APIClientTabForm, apiClientCanContinue } from './APIClientTabForm';
import { EntityTypeStatus } from './FiltersConfirmation';

const SUBMIT_ERROR_KEY = 'data-feed-submit-error';

const DataFeedFormTabs = {
  apiClient: 'apiClient',
  dataset: 'dataset',
  files: 'files',
  filters: 'filters',
  destination: 'destination',
  frequency: 'frequency',
  confirmation: 'confirmation',
} as const;

export type FormTabKeys = keyof typeof DataFeedFormTabs;

const TAB_ORDER = Object.keys(DataFeedFormTabs) as FormTabKeys[];

const isTabLocked = (tab: FormTabKeys, progress: FormTabKeys) =>
  TAB_ORDER.indexOf(tab) > TAB_ORDER.indexOf(progress);

const TabContainer = ({ children }: { children: React.ReactNode }) => {
  return <div className="w-[800px]">{children}</div>;
};

const NextTabButton = ({
  canContinue,
  onContinue,
  onCancel,
}: {
  canContinue: (form: FormInstance) => boolean;
  onContinue: () => void;
  onCancel?: () => void;
}) => {
  const form = Form.useFormInstance();

  return (
    <Form.Item shouldUpdate noStyle>
      {() => (
        <Row gutter={[16, 16]} className="mt-4">
          <Col span={24}>
            <Space>
              <DiscardDataFeedFormButton onCancel={onCancel} />
              <Button
                type="primary"
                onClick={onContinue}
                disabled={!canContinue(form)}
              >
                Continue
              </Button>
            </Space>
          </Col>
        </Row>
      )}
    </Form.Item>
  );
};

const buildSearchConfig = (
  config?: SearchConfig
): DataFeedConfig['search_config'] | undefined => {
  if (!config?.tab || !config.uuid) {
    return undefined;
  }

  return {
    id_tab: Number(config.tab.value),
    id_multi_search: config.uuid,
    top_n_orgs: Number(config.topNOrgs),
  };
};

const buildOptionalColumnsByFileType = (
  files: DataFeedForm['files'],
  optionalColumns: DataFeedForm['optionalColumns']
): DataFeedConfig['optional_columns_by_file_type'] => {
  const result: DataFeedConfig['optional_columns_by_file_type'] = {};
  files.forEach((ft) => {
    const columns = optionalColumns?.[ft];
    if (columns === undefined) return;
    result[fileTypeToNumber(ft)] = {
      columns: Object.fromEntries(columns.map((col) => [col, true])),
    };
  });
  return result;
};

const buildIdOrgRequest = (
  filters?: Partial<DataFeedFirmographicsForm>
): V2FirmographicsIdsRequestBody | undefined => {
  if (!filters) {
    return undefined;
  }

  const filtersToSave: V2FirmographicsIdsRequestBody = {};
  if (filters?.orgIds?.length) {
    filtersToSave.orgIds = filters.orgIds.map((org) => {
      const result: EntitySearchResult = JSON.parse(String(org.value));
      return result.idCbiEntity;
    });
  }

  if (filters?.orgNames?.length) {
    filtersToSave.orgNames = filters.orgNames.map((org) => {
      const result: EntitySearchResult = JSON.parse(String(org.value));
      return result.name;
    });
  }

  if (filters?.urls?.length) {
    filtersToSave.urls = filters.urls;
  }

  if (filters?.companyStatus?.length) {
    filtersToSave.orgStatusIds = filters.companyStatus.map((status) =>
      Number(status.value)
    );
  }

  if (filters?.descriptions?.length) {
    filtersToSave.descriptions = filters.descriptions;
  }

  // is_company and is_investor exist on every org doc,
  // so there is no point in adding a filter that matches all docs,
  // or one which excludes docs lacking one of those fields
  if (filters?.entityType?.value === EntityTypeStatus.COMPANIES) {
    filtersToSave.isCompany = true;
  } else if (filters?.entityType?.value === EntityTypeStatus.INVESTORS) {
    filtersToSave.isInvestor = true;
  }

  if (filters?.tier1Only) {
    filtersToSave.tier1Only = true;
  }

  if (filters?.sectors?.length) {
    filtersToSave.sectorIds = filters.sectors.map((sector) =>
      Number(sector.value)
    );
  }

  if (filters?.industries?.length) {
    filtersToSave.industryIds = filters.industries.map((industry) =>
      Number(industry.value)
    );
  }

  if (filters?.subIndustries?.length) {
    filtersToSave.subindustryIds = filters.subIndustries.map((subIndustry) =>
      Number(subIndustry.value)
    );
  }

  if (filters?.collectionIds?.length) {
    filtersToSave.collectionIds = filters.collectionIds.map((collection) =>
      Number(collection.value)
    );
  }

  if (filters?.investorTypeIds?.length) {
    filtersToSave.investorTypeIds = filters.investorTypeIds.map(
      (investorType) => Number(investorType.value)
    );
  }

  if (filters?.vcBacked !== undefined) {
    filtersToSave.vcBacked = Boolean(filters.vcBacked.value);
  }

  if (filters?.maxTotalFundingInMillions !== undefined) {
    filtersToSave.maxTotalFundingInMillions = filters.maxTotalFundingInMillions;
  }

  if (filters?.minTotalFundingInMillions !== undefined) {
    filtersToSave.minTotalFundingInMillions = filters.minTotalFundingInMillions;
  }

  if (filters?.lastFundingRoundCategoryIds?.length) {
    filtersToSave.lastFundingRoundCategoryIds =
      filters.lastFundingRoundCategoryIds.map((funding) =>
        Number(funding.value)
      );
  }

  if (filters?.minDealDate?.isValid()) {
    filtersToSave.minDealDate = filters.minDealDate.format('YYYY-MM-DD');
  }

  if (filters?.maxDealDate?.isValid()) {
    filtersToSave.maxDealDate = filters.maxDealDate.format('YYYY-MM-DD');
  }

  if (filters?.continentIds?.length) {
    filtersToSave.continentIds = filters.continentIds.map((continent) =>
      Number(continent.value)
    );
  }

  if (filters?.regionIds?.length) {
    filtersToSave.regionIds = filters.regionIds.map((region) =>
      Number(region.value)
    );
  }

  if (filters?.countries?.length) {
    filtersToSave.countryIds = filters.countries.map((country) =>
      Number(country.value)
    );
  }

  if (Object.keys(filters?.states || {}).length) {
    filtersToSave.stateProvinceIds = (filters.countries || [])
      .flatMap((country) => filters?.states?.[Number(country.value)] || [])
      .map((state) => Number(state.value));
  }

  if (filters?.technologyIds?.length) {
    filtersToSave.technologyIds = filters.technologyIds.map((t) =>
      Number(t.value)
    );
  }

  if (filters?.businessModelIds?.length) {
    filtersToSave.businessModelIds = filters.businessModelIds.map((t) =>
      Number(t.value)
    );
  }

  if (filters?.awardsAndSpotlightsIds?.length) {
    filtersToSave.awardsAndSpotlightsIds = filters.awardsAndSpotlightsIds.map(
      (t) => Number(t.value)
    );
  }

  if (Object.keys(filters?.cities || {}).length) {
    filtersToSave.cityIds = (filters.countries || []).flatMap((country) => {
      const countryId = Number(country.value);
      const states = filters?.states?.[countryId] || [];
      return states.flatMap((state) => {
        const stateId = Number(state.value);
        const cities = filters?.cities?.[countryId]?.[stateId] || [];
        return cities.map((city) => Number(city.value));
      });
    });
  }

  return filtersToSave;
};

export const DataFeedConfigurationFormTabs = ({
  teamName,
  idTeam,
  onExit,
}: {
  teamName: string;
  idTeam: string;
  onExit?: () => void;
}) => {
  const [form] = Form.useForm<DataFeedForm>();
  const [activeTab, setActiveTab] = useState<FormTabKeys>(
    DataFeedFormTabs.apiClient
  );
  const [tabProgress, setTabProgress] = useState<FormTabKeys>(
    DataFeedFormTabs.apiClient
  );

  const { mutateAsync } = useCreateDataFeedConfigMutation();
  const { isSuccess: catalogLoaded } = useGetOptionalColumnCatalog();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const moveTab = (tab: FormTabKeys) => () => {
    setActiveTab(tab);
    setTabProgress((prev) =>
      TAB_ORDER.indexOf(tab) > TAB_ORDER.indexOf(prev) ? tab : prev
    );
  };

  const updatedDataset: DataFeedForm['dataset'] = Form.useWatch(
    DataFeedFormField.dataset,
    form
  );
  const updatedFiles: DataFeedForm['files'] = Form.useWatch(
    DataFeedFormField.files,
    form
  );
  const updatedFilters: DataFeedForm['filters'] = Form.useWatch(
    DataFeedFormField.filters,
    form
  );

  useEffect(() => {
    if (activeTab !== DataFeedFormTabs.dataset || !updatedDataset) return;
    form.setFieldValue(DataFeedFormField.files, []);
    form.setFieldValue(DataFeedFormField.filters, {
      [updatedDataset]: {
        [DataFeedFirmographicsFilters.entityType]: {
          value: EntityTypeStatus.COMPANIES_AND_INVESTORS,
          label: EntityTypeStatus.COMPANIES_AND_INVESTORS,
        },
      },
    });
    setTabProgress(activeTab);
  }, [updatedDataset]);

  useEffect(() => {
    if (activeTab !== DataFeedFormTabs.files) return;
    const filesIdx = TAB_ORDER.indexOf(DataFeedFormTabs.files);
    const progressIdx = TAB_ORDER.indexOf(tabProgress);
    if (progressIdx > filesIdx && !filesCanContinue(form)) {
      setTabProgress(activeTab);
    }
  }, [updatedFiles]);

  useEffect(() => {
    if (activeTab !== DataFeedFormTabs.filters) return;
    const filtersIdx = TAB_ORDER.indexOf(DataFeedFormTabs.filters);
    const progressIdx = TAB_ORDER.indexOf(tabProgress);
    if (progressIdx > filtersIdx && !filtersCanContinue(form)) {
      setTabProgress(activeTab);
    }
  }, [updatedFilters]);

  const handleSubmit = async () => {
    setIsSubmitting(true);
    const formValues: DataFeedForm = form.getFieldsValue();

    const jsonRequest = formValues?.filters?.jsonRequest;
    const idOrgsRequest = buildIdOrgRequest(
      formValues?.filters[formValues.dataset]
    );

    let orgRequest = '';
    let searchConfig: DataFeedConfig['search_config'];

    if (jsonRequest) {
      orgRequest = jsonRequest.payload;
    } else if (idOrgsRequest) {
      orgRequest = JSON.stringify(idOrgsRequest);
    } else {
      searchConfig = buildSearchConfig(formValues?.filters?.searchConfig);
    }

    const dataFeedConfig: DataFeedConfig = {
      dataset: formValues.dataset,
      client_id: String(formValues?.client.value),
      column_exclusions: [],
      id_orgs_request: orgRequest,
      destination: {
        destination_type:
          DestinationType[
            formValues?.destination.type?.value as keyof typeof DestinationType
          ],
        path: '',
        keep_only_latest: formValues?.destination?.keepOnlyLatest,
      },
      frequency:
        FrequencyType[
          formValues?.frequency?.value as keyof typeof FrequencyType
        ],
      data_feed_name: formValues.dataFeedName,
      file_types: formValues.files,
      id_data_feed: 0,
      last_update_time: '',
      is_active: false,
      deltas: formValues?.deltas,
      search_config: searchConfig,
      replace_filename_colons: false,
      optional_columns_by_file_type: buildOptionalColumnsByFileType(
        formValues.files,
        formValues.optionalColumns
      ),
    };

    try {
      await mutateAsync(
        {
          data_feed_config: dataFeedConfig,
        },
        {
          onError: (error) => {
            notification.error({
              key: SUBMIT_ERROR_KEY,
              message: 'Failed to create the Data Feed',
              description: cleanRpcError(error),
              placement: 'top',
              duration: 10,
            });
          },
          onSuccess: ({ id_data_feed }: CreateDataFeedConfigResponse) => {
            queryClient.invalidateQueries([
              'ListDataFeedConfigResponse',
              idTeam,
            ]);
            message.success({
              content: `Data Feed ${id_data_feed} with name ${dataFeedConfig.data_feed_name} created successfully`,
              style: {
                marginTop: '3vh',
              },
            });

            if (onExit) {
              onExit();
            } else {
              navigate(`${DataFeedRoutes.DataFeed}/${idTeam}`);
            }
          },
        }
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getTabItems = useCallback(() => {
    return [
      {
        key: DataFeedFormTabs.apiClient,
        label: 'API Client',
        children: (
          <TabContainer>
            <APIClientTabForm idTeam={idTeam} />
            <NextTabButton
              canContinue={apiClientCanContinue}
              onContinue={moveTab(DataFeedFormTabs.dataset)}
              onCancel={onExit}
            />
          </TabContainer>
        ),
        disabled: false,
      },
      {
        key: DataFeedFormTabs.dataset,
        label: 'Dataset',
        children: (
          <TabContainer>
            <DatasetTabForm />
            <NextTabButton
              canContinue={datasetCanContinue}
              onContinue={moveTab(DataFeedFormTabs.files)}
              onCancel={onExit}
            />
          </TabContainer>
        ),
        disabled: isTabLocked(DataFeedFormTabs.dataset, tabProgress),
      },
      {
        key: DataFeedFormTabs.files,
        label: 'Dataset Files',
        children: (
          <TabContainer>
            <DataFeedFiles />
            <NextTabButton
              canContinue={(f) => catalogLoaded && filesCanContinue(f)}
              onContinue={moveTab(DataFeedFormTabs.filters)}
              onCancel={onExit}
            />
          </TabContainer>
        ),
        disabled: isTabLocked(DataFeedFormTabs.files, tabProgress),
      },
      {
        key: DataFeedFormTabs.filters,
        label: 'Filters',
        children: (
          <>
            <FiltersTab />
            <NextTabButton
              canContinue={filtersCanContinue}
              onContinue={moveTab(DataFeedFormTabs.destination)}
              onCancel={onExit}
            />
          </>
        ),
        disabled: isTabLocked(DataFeedFormTabs.filters, tabProgress),
      },
      {
        key: DataFeedFormTabs.destination,
        label: 'Destination',
        children: (
          <TabContainer>
            <DataFeedDestinationTabForm />
            <NextTabButton
              canContinue={destinationCanContinue}
              onContinue={moveTab(DataFeedFormTabs.frequency)}
              onCancel={onExit}
            />
          </TabContainer>
        ),
        disabled: isTabLocked(DataFeedFormTabs.destination, tabProgress),
      },
      {
        key: DataFeedFormTabs.frequency,
        label: 'Frequency',
        children: (
          <TabContainer>
            <DataFeedFrequencyTabForm />
            <NextTabButton
              canContinue={frequencyCanContinue}
              onContinue={moveTab(DataFeedFormTabs.confirmation)}
              onCancel={onExit}
            />
          </TabContainer>
        ),
        disabled: isTabLocked(DataFeedFormTabs.frequency, tabProgress),
      },
      {
        key: DataFeedFormTabs.confirmation,
        label: 'Confirmation',
        children: (
          <TabContainer>
            <DataFeedConfirmationTabForm
              setActiveTab={setActiveTab}
              teamName={teamName}
            />
            <Row gutter={[16, 16]} className="mt-4">
              <Col span={24}>
                <Space>
                  <DiscardDataFeedFormButton onCancel={onExit} />
                  <Button
                    type="primary"
                    loading={isSubmitting}
                    onClick={handleSubmit}
                  >
                    Save Data Feed
                  </Button>
                </Space>
              </Col>
            </Row>
          </TabContainer>
        ),
        disabled: isTabLocked(DataFeedFormTabs.confirmation, tabProgress),
      },
    ];
  }, [
    tabProgress,
    setActiveTab,
    teamName,
    isSubmitting,
    onExit,
    idTeam,
    catalogLoaded,
  ]);

  const initialValues = {
    deltas: true,
    destination: {
      type: {
        value: DestinationType.DESTINATION_SFTP_INTERNAL,
        label: DestinationTypeLabels[DestinationType.DESTINATION_SFTP_INTERNAL],
      },
    },
  };

  return (
    <Form form={form} layout="vertical" initialValues={initialValues}>
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab as $TSFixMe}
        items={getTabItems()}
      />
    </Form>
  );
};
